import type { GameModeId } from './types';

export interface GameModeDefinition {
  id: GameModeId;
  name: string;
  shortDescription: string;
  description: string;
  startingLevel: number;
  linesPerLevel: number;
  fixedGravityMs: number | null;
  holdEnabled: boolean;
  ghostEnabled: boolean;
  previewCount: number;
  gravityEnabled?: boolean;
  scoringRule?: 'classic' | 'contract';
}

export const GAME_MODES: readonly GameModeDefinition[] = [
  {
    id: 'regular',
    name: 'Regular',
    shortDescription: 'Classic curve',
    description: 'The original rules. Speed increases every 10 lines.',
    startingLevel: 1,
    linesPerLevel: 10,
    fixedGravityMs: null,
    holdEnabled: true,
    ghostEnabled: true,
    previewCount: 3,
  },
  {
    id: 'zen',
    name: 'Zen',
    shortDescription: 'Fixed tempo',
    description: 'Clear lines at your own pace. Gravity never speeds up.',
    startingLevel: 1,
    linesPerLevel: 10,
    fixedGravityMs: 900,
    holdEnabled: true,
    ghostEnabled: true,
    previewCount: 3,
  },
  {
    id: 'rush',
    name: 'Rush',
    shortDescription: 'Quick climb',
    description: 'A steeper challenge that speeds up every 5 lines.',
    startingLevel: 1,
    linesPerLevel: 5,
    fixedGravityMs: null,
    holdEnabled: true,
    ghostEnabled: true,
    previewCount: 3,
  },
  {
    id: 'survival',
    name: 'Survival',
    shortDescription: 'No safety net',
    description: 'Start at level 5 and survive without using hold.',
    startingLevel: 5,
    linesPerLevel: 10,
    fixedGravityMs: null,
    holdEnabled: false,
    ghostEnabled: true,
    previewCount: 3,
  },
  {
    id: 'fog',
    name: 'Fog',
    shortDescription: 'No assists',
    description: 'No ghost piece or next previews. The board is all you get.',
    startingLevel: 1,
    linesPerLevel: 10,
    fixedGravityMs: null,
    holdEnabled: true,
    ghostEnabled: false,
    previewCount: 0,
  },
  {
    id: 'overdrive',
    name: 'Overdrive',
    shortDescription: 'Rapid ascent',
    description: 'Start at level 3 and speed up every 3 cleared lines.',
    startingLevel: 3,
    linesPerLevel: 3,
    fixedGravityMs: null,
    holdEnabled: true,
    ghostEnabled: true,
    previewCount: 3,
  },
  {
    id: 'turbo',
    name: 'Turbo',
    shortDescription: 'Locked fast',
    description: 'Start at level 7 with gravity locked at a relentless pace.',
    startingLevel: 7,
    linesPerLevel: 10,
    fixedGravityMs: 275,
    holdEnabled: true,
    ghostEnabled: true,
    previewCount: 3,
  },
  {
    id: 'draft',
    name: 'Draft',
    shortDescription: 'Plan one ahead',
    description: 'Only one upcoming piece is visible, and hold is disabled.',
    startingLevel: 1,
    linesPerLevel: 10,
    fixedGravityMs: null,
    holdEnabled: false,
    ghostEnabled: true,
    previewCount: 1,
  },
  {
    id: 'contract',
    name: 'Contract',
    shortDescription: 'Hit the target',
    description: 'Clear exactly 1, 2, 3, then 4 lines. Hits earn triple line score; misses earn none.',
    startingLevel: 1,
    linesPerLevel: 10,
    fixedGravityMs: null,
    holdEnabled: true,
    ghostEnabled: true,
    previewCount: 3,
    scoringRule: 'contract',
  },
  {
    id: 'zeroG',
    name: 'Zero-G',
    shortDescription: 'You set the pace',
    description: 'Pieces never fall automatically. Use soft or hard drop to place them.',
    startingLevel: 1,
    linesPerLevel: 10,
    fixedGravityMs: null,
    holdEnabled: true,
    ghostEnabled: true,
    previewCount: 3,
    gravityEnabled: false,
  },
] as const;

export function getGameMode(id: GameModeId): GameModeDefinition {
  return GAME_MODES.find((mode) => mode.id === id) ?? GAME_MODES[0];
}

export function levelForLines(modeId: GameModeId, lines: number): number {
  const mode = getGameMode(modeId);
  return mode.startingLevel + Math.floor(lines / mode.linesPerLevel);
}
